import { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText,
  SquareArrowOutUpRight,
  Image as ImageIcon,
  Mail,
  TriangleAlert,
  ArrowDownToLine,
  ArrowUpToLine,
  Star,
} from 'lucide-react';
import { Card, HStack, VStack } from '@components/base/layout';
import { Text, Badge, Icon } from '@components/base/display';
import { Button, ButtonGroup } from '@components/base/actions';
import { Select, type SelectOption } from '@components/base/inputs';
import { Alert, Spinner } from '@components/base/feedback';
import {
  DataList,
  useDataList,
  dataListWorkflowRegistry,
} from '@components/integrated/data-list';
import type { DataListConfig, FilterConfig } from '@components/integrated/data-list';
import type { ActionsConfig, CustomActionConfig } from '@components/integrated/entity-form';
import {
  applyFilterOverrides,
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import {
  documentsService,
  type DocumentsListFilters,
  type DocumentsTabAdoc,
  type GedCategoryOption,
  type VeosDocumentRow,
  type VeosClassementOption,
} from '../api/documentsService';
import { DocumentPreviewConfigurable } from './documents/DocumentPreview';
import { EmailDetail } from './documents/EmailDetail';
import {
  getDocumentKind,
  getDocumentTypeLabel,
  getDocumentSens,
  getDocumentCreator,
  getDocumentDateSortKey,
  isTaskLinkedDocument,
} from './documents/documentHelpers';
import {
  broadcastEmailReplyRequest,
  broadcastPopoutClaim,
  buildDocumentsPopoutUrl,
  focusOpenerWindow,
  openDocumentsPopout,
} from './documents/documentsPopout';
import { useClaimDataStore, type EmailReplyRequestInput } from './claimDataStore';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentsCardProps {
  ses: string;
  idSin: string;
  /**
   * The current task id (`ID_EVENT`), forwarded to the documents boby as
   * `id_event` so VEOS can flag which rows are attached to the task
   * (`DOC_TACHE` → the "Statut" star + "Nouveau"). Optional: when omitted the
   * card falls back to the `taskId` route param. The popout window passes this
   * explicitly because its URL param goes stale after a task switch (it syncs
   * via BroadcastChannel, not navigation); the claim screen passes it too, as
   * its task may come from `?task=` rather than the route. No task at all (a
   * claim opened from the claim search, mahhp 22861) ⇒ no `id_event`, and the
   * popout opens on the task-less popout route.
   */
  idEvent?: string;
  /**
   * Claim metadata used only to build the popout window's page header
   * ("Documents · Dossier {dossierNumber}" + `client` subtitle — see
   * `buildPopoutHeaderTitle`). Forwarded verbatim into the popout URL and the
   * sync broadcast; the main-window card does not render them itself. All
   * optional — the popout header omits whatever is missing.
   *
   * - `dossierNumber` is the claim's `NCBT_NCIE`, which VEOS usually returns as
   *   a composite "{NCBT_SIN} | {company ref}" (e.g. "24337096 |
   *   B24H00106400") — it already carries the claim number;
   * - `contractRef` is the claim's `NCBT_SIN` (the claim number itself, despite
   *   the prop name). The title appends it only when `dossierNumber` does not
   *   already contain it, so it is never shown twice;
   * - `client` is the claim's client — the insured, `PASS_NOM_PER`
   *   (`ClaimSummary.client`), not the apporteur / insurance company
   *   (`PAPP_NOM_PER`).
   */
  dossierNumber?: string;
  contractRef?: string;
  client?: string;
  /**
   * Per-tenant layout overrides injected by the `withComponentRules` HOC.
   * Underscored to match the injected meta-prop convention; `undefined` at
   * baseline ⇒ the configs are returned unchanged (byte-identical render).
   * Rule authors target stable keys only:
   *
   * - `columns` — the list columns `kind` / `type` / `sens` / `libelle` /
   *   `category` / `date` / `creator` / `statut` (+ the hidden `fileName`):
   *   rename / hide / reorder / tooltip;
   * - `filters` — the header filters `scope` (Prod | Sin toggle) and
   *   `category` (category select): rename (accessible name), re-placeholder
   *   (the "Toutes les catégories" entry), hide, reorder;
   * - `actions` — the header `popOut` ("Ouvrir") button: relabel / hide.
   *
   * The card keeps owning fetch, filtering, selection, and the detail panel,
   * so a layout override can never affect the open / GED / refresh machinery.
   */
  _layoutOverrides?: LayoutOverrides;
  /**
   * When `true`, the header "Ouvrir" pop-out button is not rendered and the
   * card does not broadcast task changes: there is no point offering to pop out
   * a panel that is already in its own window, and the popup is a sync
   * *receiver*, not a source. Omitted (⇒ `false`) at the main render site. The
   * Prod | Sin and category filters stay available in the popout. Implied by
   * {@link DocumentsCardProps.inPopoutWindow}, which the popout page now sets.
   */
  hidePopOut?: boolean;
  /**
   * `true` when the card is rendered by the Documents popout window
   * (`DocumentsPopoutPage`). Implies `hidePopOut`, and routes the e-mail
   * "Répondre" button (mahhp 22227) to the MAIN window — there is no Email card
   * in the popout: the request goes over the popout BroadcastChannel (the main
   * claim view relays it to its Email card) and the popout then tries to focus
   * its opener. Omitted (⇒ `false`) at the main render site, where "Répondre"
   * asks the same window's Email card through `useClaimDataStore`.
   */
  inPopoutWindow?: boolean;
}

/** Card-level texts (status / empty states), kept out of the JSX. */
const DOCUMENTS_CARD_TEXT = {
  title: 'Documents',
  loadError: 'Erreur lors du chargement des documents.',
  empty: 'Aucun document.',
  emptyForCategory: 'Aucun document dans cette catégorie.',
  noSelection: 'Sélectionnez un document',
} as const;

/** Label of the uncategorized pill — also its sort key, so those rows group together. */
const UNCATEGORIZED_LABEL = 'Sans catégorie';

// ---------------------------------------------------------------------------
// Header controls config (mahhp 22227)
//
// Right-aligned in the card header, in this order: the "Prod | Sin" GED scope
// toggle, the category filter, then the "Ouvrir" pop-out button. Every label /
// placeholder / tooltip lives here — never inline in JSX — under stable keys:
//
// - the two filters are `FilterConfig`s keyed `scope` / `category`. They are
//   published with the card's layout config and merged with
//   `_layoutOverrides.filters`, so a tenant rule can rename (accessible name),
//   re-placeholder (the "Toutes les catégories" entry), hide, or reorder them
//   (`renameFilter` / `setFilterPlaceholder` / `hideFilter` / `reorderFilters`).
//   They filter SERVER-side (the card refetches with `tab_adoc` / `cd_ds`), so
//   they are deliberately not in the DataList config's own `toolbar.filters`
//   (which would filter the loaded rows client-side);
// - the pop-out button is a custom action keyed `popOut`, published under
//   `actions` and merged with `_layoutOverrides.actions` (`setActionLabel` /
//   `hideAction`). The card wires its click itself; `handler` only names it.
// ---------------------------------------------------------------------------

const SCOPE_FILTER_KEY = 'scope';
const CATEGORY_FILTER_KEY = 'category';
const POP_OUT_ACTION_KEY = 'popOut';

/** Default GED scope: the claim's documents ("Sin"). */
const DEFAULT_TAB_ADOC: DocumentsTabAdoc = 'SINISTRE';

/** Category-select value standing for "no category filter" (never a VEOS `CD_DS`). */
const ALL_CATEGORIES_VALUE = '__all__';

const DOCUMENTS_HEADER_FILTERS: readonly FilterConfig[] = [
  {
    id: SCOPE_FILTER_KEY,
    type: 'select',
    label: 'Documents du contrat ou du sinistre',
    order: 1,
    paramName: 'tab_adoc',
    defaultValue: DEFAULT_TAB_ADOC,
    options: [
      { value: 'POLICE', label: 'Prod' },
      { value: 'SINISTRE', label: 'Sin' },
    ],
  },
  {
    id: CATEGORY_FILTER_KEY,
    type: 'select',
    label: 'Filtrer par catégorie',
    placeholder: 'Toutes les catégories',
    order: 2,
    paramName: 'cd_ds',
  },
];

const POP_OUT_ACTION: CustomActionConfig = {
  id: POP_OUT_ACTION_KEY,
  handler: 'claimflow-documentsPopOut',
  label: 'Ouvrir',
  variant: 'secondary',
  size: 'sm',
};

interface DocumentsHeaderConfig {
  /** Header filters, rule-targetable through `_layoutOverrides.filters`. */
  filters: readonly FilterConfig[];
  /** Header buttons, rule-targetable through `_layoutOverrides.actions`. */
  actions: ActionsConfig;
  /** Hover hints (no override bucket targets them). */
  tooltips: { scope: Record<DocumentsTabAdoc, string>; popOut: string };
  /** The category select gets a search box above this many categories. */
  categorySearchThreshold: number;
}

const DOCUMENTS_HEADER_CONFIG: DocumentsHeaderConfig = {
  filters: DOCUMENTS_HEADER_FILTERS,
  actions: { custom: [POP_OUT_ACTION] },
  tooltips: {
    scope: {
      POLICE: 'Documents du contrat',
      SINISTRE: 'Documents du sinistre',
    },
    popOut: 'Ouvrir les documents dans une nouvelle fenêtre',
  },
  categorySearchThreshold: 8,
};

function isDocumentsTabAdoc(value: string): value is DocumentsTabAdoc {
  return value === 'SINISTRE' || value === 'POLICE';
}

/** Rendering order of the visible header filters (by `order`, then declaration). */
function visibleHeaderFilters(filters: readonly FilterConfig[]): FilterConfig[] {
  return filters
    .filter((f) => f.hidden !== true)
    .sort(
      (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER),
    );
}

/** Server-side filters of the documents list — reset whenever the claim changes. */
interface DocumentsFilterState {
  /** The claim these filters were chosen for. */
  idSin: string;
  tabAdoc: DocumentsTabAdoc;
  /** Selected category (`cd_ds`), `null` = every category. */
  category: GedCategoryOption | null;
}

function defaultFilters(idSin: string): DocumentsFilterState {
  return { idSin, tabAdoc: DEFAULT_TAB_ADOC, category: null };
}

const NO_CATEGORIES: GedCategoryOption[] = [];

// ---------------------------------------------------------------------------
// Per-row render context
//
// Custom cell renderers + the row-click handler are module-singletons
// (registered once below), but selection must stay controlled by the card's
// live `selectedId`. Each row carries its per-instance state under `__ctx`,
// which the singletons read from `row` at render / click time. Rows are
// rebuilt every render (see `rows` useMemo) so the closures stay fresh —
// no module-global mutable state, multi-instance safe (mirrors
// `MissingPiecesCard`).
// ---------------------------------------------------------------------------

interface DocumentRowContext {
  /** Whether this row is the currently selected one. */
  active: boolean;
  /** Select this row (client-side only — drives the detail panel). */
  onSelect: () => void;
}

/**
 * Raw, comparable values the DataList sorts the custom-rendered columns on
 * (mahhp 22227). Mapped per column key through `dataSource.fieldMapping`
 * (`__sort.<key>`), so each cell keeps rendering from the row while the header
 * click sorts on a meaningful value. `null` sorts last in both directions.
 */
interface DocumentSortKeys {
  /** The "PDF" / "IMAGE" / "MAIL" / "FICHIER" label shown in the Type column. */
  type: string;
  /** `entrant` / `sortant`, `null` when VEOS did not tag the row. */
  sens: string | null;
  /** The category label; uncategorized rows share the "Sans catégorie" label. */
  category: string | null;
  /** Chronological ISO key derived from the French `DAT_ADOC` display string. */
  date: string | null;
  /** The (typo-tolerant) creator name the Créateur column shows. */
  creator: string | null;
  /** `0` for task-linked ("Nouveau") rows so they lead on the first click. */
  statut: number;
}

function buildSortKeys(doc: VeosDocumentRow): DocumentSortKeys {
  const creator = getDocumentCreator(doc);
  const categoryLabel = doc.LIB_DS?.trim() ?? '';
  let category: string | null = null;
  if (doc.ACATEGORISER === 1) category = UNCATEGORIZED_LABEL;
  else if (categoryLabel !== '') category = categoryLabel;
  return {
    type: getDocumentTypeLabel(doc),
    sens: getDocumentSens(doc),
    category,
    date: getDocumentDateSortKey(doc.DAT_ADOC),
    creator: creator === '' ? null : creator,
    statut: isTaskLinkedDocument(doc) ? 0 : 1,
  };
}

interface DocumentRow extends VeosDocumentRow {
  /** Stable React key for the DataList table (it keys rows by `row.id`). */
  id: number;
  /** Sort values for the custom-rendered columns (see {@link DocumentSortKeys}). */
  __sort: DocumentSortKeys;
  /**
   * Row-level marker read by `formatting.rowRules` to apply the active-row
   * highlight (left primary border + `bg-secondary-100`). Mirrors the
   * `selectedId` so a rebuilt config reflects the new selection.
   */
  __active: boolean;
  __ctx: DocumentRowContext;
}

function getRowContext(row: Record<string, unknown>): DocumentRowContext | undefined {
  return (row as Partial<DocumentRow>).__ctx ?? undefined;
}

// ---------------------------------------------------------------------------
// Custom cell renderers + row-click handler (registered once at module load)
//
// They reuse the existing classification helpers (`getDocumentKind`,
// `getDocumentTypeLabel`) and the `DocumentListItem` category / new-dot logic
// verbatim — no re-implementation of classification.
// ---------------------------------------------------------------------------

const KIND_CELL_RENDERER = 'claimflow-documentKindIcon';
const TYPE_CELL_RENDERER = 'claimflow-documentTypeLabel';
const SENS_CELL_RENDERER = 'claimflow-documentSens';
const CATEGORY_CELL_RENDERER = 'claimflow-documentCategory';
const DATE_CELL_RENDERER = 'claimflow-documentDate';
const CREATOR_CELL_RENDERER = 'claimflow-documentCreator';
const STATUT_CELL_RENDERER = 'claimflow-documentStatut';
const ROW_CLICK_HANDLER = 'claimflow-documentSelect';

dataListWorkflowRegistry.registerCellRenderer(KIND_CELL_RENDERER, (_value, row) => {
  const doc = row as unknown as VeosDocumentRow;
  const kind = getDocumentKind(doc);
  const active = getRowContext(row)?.active ?? false;
  // Active row keeps the original darker tone; `aria-current` lives on this
  // icon wrapper because the DataList `<tr>` exposes no aria hook (see Notes).
  const tone = active ? 'text-secondary-600' : 'text-secondary-400';
  let IconCmp = FileText;
  if (kind === 'email') IconCmp = Mail;
  else if (kind === 'image') IconCmp = ImageIcon;
  return (
    <span aria-current={active ? 'true' : undefined} className="inline-flex">
      <IconCmp className={`w-4 h-4 shrink-0 ${tone}`} />
    </span>
  );
});

dataListWorkflowRegistry.registerCellRenderer(TYPE_CELL_RENDERER, (_value, row) => {
  const doc = row as unknown as VeosDocumentRow;
  return (
    <Text as="span" size="xs" weight="semibold" color="muted" className="tracking-wide">
      {getDocumentTypeLabel(doc)}
    </Text>
  );
});

// "Sens" column: a received document (ENTRANT) shows a green download arrow, a
// sent one (SORTANT) a blue upload arrow. A row VEOS did not tag (no `SENS`)
// renders nothing rather than a misleading default.
dataListWorkflowRegistry.registerCellRenderer(SENS_CELL_RENDERER, (_value, row) => {
  const doc = row as unknown as VeosDocumentRow;
  const sens = getDocumentSens(doc);
  if (!sens) return null;
  const entrant = sens === 'entrant';
  const Arrow = entrant ? ArrowDownToLine : ArrowUpToLine;
  const tone = entrant ? 'text-success-600' : 'text-primary-600';
  const label = entrant ? 'Entrant' : 'Sortant';
  return (
    <span className="inline-flex" title={label}>
      <Arrow className={`w-4 h-4 shrink-0 ${tone}`} aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
});

// "Date" column: VEOS' French display string (e.g. "05/06/20"), shown verbatim —
// a `date` column would re-parse it (brittle with 2-digit years). The column
// SORTS on the chronological `__sort.date` key mapped in `fieldMapping`, so the
// cell reads the display value from the row itself (same markup as a text cell).
dataListWorkflowRegistry.registerCellRenderer(DATE_CELL_RENDERER, (_value, row) => {
  const doc = row as unknown as VeosDocumentRow;
  return doc.DAT_ADOC ? <span>{doc.DAT_ADOC}</span> : null;
});

// "Créateur" column: the user who filed the document (typo-tolerant lookup).
dataListWorkflowRegistry.registerCellRenderer(CREATOR_CELL_RENDERER, (_value, row) => {
  const doc = row as unknown as VeosDocumentRow;
  const creator = getDocumentCreator(doc);
  if (!creator) return null;
  return (
    <Text as="span" size="sm" color="muted">
      {creator}
    </Text>
  );
});

// "Statut" column: documents attached to the current task (DOC_TACHE=1) — the
// ones that gave rise to it — are flagged with a star + "Nouveau" pill. Others
// render nothing.
dataListWorkflowRegistry.registerCellRenderer(STATUT_CELL_RENDERER, (_value, row) => {
  const doc = row as unknown as VeosDocumentRow;
  if (!isTaskLinkedDocument(doc)) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Star className="w-4 h-4 shrink-0 fill-warning-400 text-warning-400" aria-hidden="true" />
      <span className="inline-flex items-center rounded-full border border-warning-400 bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-700">
        Nouveau
      </span>
    </span>
  );
});

dataListWorkflowRegistry.registerCellRenderer(CATEGORY_CELL_RENDERER, (_value, row) => {
  const doc = row as unknown as VeosDocumentRow;
  const uncategorized = doc.ACATEGORISER === 1;

  if (uncategorized) {
    // Outlined amber "Sans catégorie" pill. The old trailing "unread" dot was
    // removed in mahhp 22268: the per-row marker now lives in the dedicated
    // "Statut" column (star + "Nouveau"), driven by DOC_TACHE, not by the
    // uncategorized flag.
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-warning-400 bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-700">
        <TriangleAlert className="w-3 h-3 shrink-0" />
        {UNCATEGORIZED_LABEL}
      </span>
    );
  }

  const label = doc.LIB_DS;
  if (!label) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-700">
      {label}
    </span>
  );
});

dataListWorkflowRegistry.registerRowClickHandler(ROW_CLICK_HANDLER, (row) => {
  getRowContext(row)?.onSelect();
});

// ---------------------------------------------------------------------------
// Config factory
//
// Declarative DataListConfig with stable column keys. French labels live only
// in `column.label` (display-only / overridable). The list keeps the API order
// until the user clicks a sortable header (client-side, single-column toggle
// none → asc → desc — mahhp 22227); custom-rendered columns sort on the raw
// `__sort.*` values mapped in `fieldMapping`. No toolbar, every row on one page
// (parity with the original scroll container), and the active-row highlight is
// driven from the per-row `__active` marker via `formatting.rowRules`.
// ---------------------------------------------------------------------------

function createDocumentsListConfig(rows: DocumentRow[]): DataListConfig {
  return {
    id: 'claimflow.documentsCard',

    dataSource: {
      data: rows as unknown as Record<string, unknown>[],
      // Cell value per column key — what the column displays (text columns) or
      // sorts on (custom columns, which render from the row itself).
      fieldMapping: {
        type: '__sort.type',
        sens: '__sort.sens',
        libelle: 'LIB_ADOC',
        category: '__sort.category',
        date: '__sort.date',
        creator: '__sort.creator',
        statut: '__sort.statut',
        fileName: 'NOM_ADOC',
      },
    },

    columns: {
      kind: {
        label: '',
        order: 1,
        align: 'left',
        width: '40px',
        type: 'custom',
        typeOptions: { renderer: KIND_CELL_RENDERER },
        sortable: false,
      },
      type: {
        label: 'Type',
        order: 2,
        align: 'left',
        type: 'custom',
        typeOptions: { renderer: TYPE_CELL_RENDERER },
        sortable: true,
      },
      sens: {
        label: 'Sens',
        order: 3,
        align: 'center',
        width: '56px',
        type: 'custom',
        typeOptions: { renderer: SENS_CELL_RENDERER },
        sortable: true,
      },
      libelle: {
        label: 'Libellé',
        order: 4,
        align: 'left',
        type: 'text',
        sortable: true,
      },
      category: {
        label: 'Catégorie',
        order: 5,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: CATEGORY_CELL_RENDERER },
        sortable: true,
      },
      date: {
        label: 'Date',
        order: 6,
        align: 'center',
        type: 'custom',
        typeOptions: { renderer: DATE_CELL_RENDERER },
        sortable: true,
      },
      creator: {
        label: 'Créateur',
        order: 7,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: CREATOR_CELL_RENDERER },
        sortable: true,
      },
      statut: {
        label: 'Statut',
        order: 8,
        align: 'right',
        type: 'custom',
        typeOptions: { renderer: STATUT_CELL_RENDERER },
        sortable: true,
      },
      fileName: {
        label: 'Fichier',
        order: 9,
        hidden: true,
        align: 'left',
        type: 'text',
        sortable: false,
      },
    },

    display: {
      size: 'compact',
      hoverable: true,
    },

    // Front-only sorting on the loaded rows (no API call); API order until the
    // user clicks a header.
    sorting: {
      enabled: true,
    },

    toolbar: {
      visible: false,
    },

    actions: {
      rowClick: { handler: ROW_CLICK_HANDLER },
    },

    // Active-row highlight (left primary border + tinted background), driven
    // from the per-row `__active` marker. Every row gets a transparent left
    // border via `cssOverrides.row` so the active border does not shift layout.
    formatting: {
      rowRules: [
        {
          condition: { field: '__active', operator: 'equals', value: true },
          className: 'border-l-4 border-l-primary-600 bg-secondary-100',
        },
      ],
    },

    cssOverrides: {
      row: 'border-l-4 border-l-transparent',
    },

    // Single short list — render everything on one page (no pager); the card
    // caps the visible height with its own scroll container below.
    pagination: {
      mode: 'scroll',
      pageSize: 999_999,
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentsCard({
  ses,
  idSin,
  idEvent,
  dossierNumber,
  contractRef,
  client,
  hidePopOut,
  inPopoutWindow,
  _layoutOverrides,
}: Readonly<DocumentsCardProps>) {
  // The popout window is a sync receiver without its own "Ouvrir" button.
  const isPopoutWindow = inPopoutWindow === true;
  const suppressPopOut = hidePopOut === true || isPopoutWindow;

  const [documents, setDocuments] = useState<VeosDocumentRow[]>([]);
  const [classementOptions, setClassementOptions] = useState<VeosClassementOption[]>([]);
  // Request key (claim + task + filters) of the documents response on screen;
  // `null` until the very first request settles (⇒ the full-card spinner).
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Category options, tagged with the (claim + scope) they were fetched for so
  // a stale list is never offered after a Prod | Sin switch.
  const [categoriesState, setCategoriesState] = useState<{
    key: string;
    options: GedCategoryOption[];
  } | null>(null);

  // Header filters (Prod | Sin + category). They belong to one claim: when the
  // card is handed another `idSin` (e.g. the popout following a task switch in
  // the main window) they reset to the defaults — adjusted during render, so
  // the new claim's first request already uses the defaults.
  const [filters, setFilters] = useState<DocumentsFilterState>(() => defaultFilters(idSin));
  let activeFilters = filters;
  if (filters.idSin !== idSin) {
    activeFilters = defaultFilters(idSin);
    setFilters(activeFilters);
  }
  const { tabAdoc, category } = activeFilters;
  const cdDs = category?.code ?? '';

  // The current task lives in the URL; the "Ouvrir" header button pops the whole
  // Documents panel out into a synchronised popup window keyed on these params.
  const { tenantSlug, taskId } = useParams<{ tenantSlug: string; taskId: string }>();

  // Task id forwarded to VEOS as `id_event` (drives the DOC_TACHE "Statut"
  // flag). The explicit `idEvent` prop wins (the popout passes the live task id
  // that its stale URL param would miss); otherwise the route's `taskId`.
  const idEventForFetch = idEvent ?? taskId;

  const documentsKey = JSON.stringify([idSin, idEventForFetch ?? '', tabAdoc, cdDs]);
  const categoriesKey = JSON.stringify([idSin, tabAdoc]);

  // Stale-response guards: every request takes a new sequence number and only
  // the latest one issued may write state, so a slow response for filters the
  // user already left (or a post-save refresh overtaken by a filter change)
  // never overwrites a newer result.
  const documentsRequestRef = useRef(0);
  const categoriesRequestRef = useRef(0);

  // Documents for the current claim / task / filters (server-side `tab_adoc`
  // + `cd_ds`).
  const loadDocuments = useCallback(() => {
    const requestId = ++documentsRequestRef.current;
    const listFilters: DocumentsListFilters = { tabAdoc, ...(cdDs ? { cdDs } : {}) };
    return documentsService.fetchDocuments(idSin, idEventForFetch, listFilters).then(
      (docs) => {
        if (requestId !== documentsRequestRef.current) return;
        setDocuments(docs);
        setLoadError(null);
        setSettledKey(documentsKey);
      },
      () => {
        if (requestId !== documentsRequestRef.current) return;
        setLoadError(DOCUMENTS_CARD_TEXT.loadError);
        setSettledKey(documentsKey);
      },
    );
  }, [idSin, idEventForFetch, tabAdoc, cdDs, documentsKey]);

  // Categories present in the current claim's GED for the current scope (the
  // category select's options). A failure keeps whatever is shown — the
  // select still offers "Toutes les catégories".
  const loadCategories = useCallback(() => {
    const requestId = ++categoriesRequestRef.current;
    return documentsService.fetchGedCategories(idSin, tabAdoc).then(
      (options) => {
        if (requestId !== categoriesRequestRef.current) return;
        setCategoriesState({ key: categoriesKey, options });
      },
      () => undefined,
    );
  }, [idSin, tabAdoc, categoriesKey]);

  // "Classement" options of the detail panel (`WS2_GS_SEL_CDDS`) — global, so
  // fetched once and on refresh. A failure keeps the current options.
  const loadClassementOptions = useCallback(
    () =>
      documentsService.fetchClassementOptions().then(
        (options) => {
          setClassementOptions(options);
        },
        () => undefined,
      ),
    [],
  );

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    void loadClassementOptions();
  }, [loadClassementOptions]);

  // Refresh the list (with the CURRENT filters), the categories, and the
  // Classement options without flashing the spinner — used after a save / GED
  // so row badges + labels reflect the new state.
  const refresh = useCallback(async () => {
    await Promise.all([loadDocuments(), loadCategories(), loadClassementOptions()]);
  }, [loadDocuments, loadCategories, loadClassementOptions]);

  const isInitialLoading = settledKey === null;
  const isRefetching = !isInitialLoading && settledKey !== documentsKey;

  const categories =
    categoriesState?.key === categoriesKey ? categoriesState.options : NO_CATEGORIES;

  const selectedRow = useMemo(
    () => documents.find((d) => d.ID_ADOC === selectedId) ?? null,
    [documents, selectedId],
  );

  const selectedKind = selectedRow ? getDocumentKind(selectedRow) : null;

  // Broadcast the current claim so an already-open popout window follows the
  // active task: whenever the user opens a different task in the main window
  // this card remounts with new ids and re-broadcasts, and the popup refetches.
  // Skipped inside the popup (`hidePopOut` / `inPopoutWindow`) — it is the
  // receiver, not a source.
  useEffect(() => {
    if (suppressPopOut) return;
    if (!ses || !idSin) return;
    broadcastPopoutClaim({
      ses,
      idSin,
      taskId: idEventForFetch,
      tenantSlug,
      dossierNumber,
      contractRef,
      client,
    });
  }, [suppressPopOut, ses, idSin, idEventForFetch, tenantSlug, dossierNumber, contractRef, client]);

  // "Répondre" on a received e-mail (mahhp 22227): open the claim view's Email
  // card in reply to it. In the main window the request goes through the
  // claim-data store (the Email card consumes it); the popout window has no
  // Email card, so it posts the request to the main window over the popout
  // channel and tries to bring that window to the front. The request carries
  // the claim id — the Email card ignores a request for another claim (e.g.
  // from a popout gone stale).
  const requestEmailReply = useClaimDataStore((s) => s.requestEmailReply);
  const handleEmailReply = useCallback(
    (row: VeosDocumentRow) => {
      const request: EmailReplyRequestInput = {
        idSin,
        idAdoc: String(row.ID_ADOC),
        label: row.LIB_ADOC,
      };
      if (isPopoutWindow) {
        broadcastEmailReplyRequest(request);
        focusOpenerWindow();
        return;
      }
      requestEmailReply(request);
    },
    [idSin, isPopoutWindow, requestEmailReply],
  );

  // Header "Ouvrir": pop the whole Documents panel out into its own popup window
  // (or focus the existing one). The popup boots its own SPA instance at the
  // dedicated route, so it is authenticated + brand-themed automatically. The
  // claim metadata rides along in the URL so the popout's header shows on first
  // open (the broadcast then keeps it in sync across task switches).
  const handlePopOut = useCallback(() => {
    if (!tenantSlug) return;
    openDocumentsPopout(
      buildDocumentsPopoutUrl({
        tenantSlug,
        taskId: idEventForFetch,
        ses,
        idSin,
        dossierNumber,
        contractRef,
        client,
      }),
    );
  }, [tenantSlug, idEventForFetch, ses, idSin, dossierNumber, contractRef, client]);

  // Prod | Sin: switching scope changes the GED, hence the category list — the
  // category filter resets to "Toutes" (documents + categories refetch).
  const handleTabAdocChange = useCallback((next: DocumentsTabAdoc) => {
    setFilters((prev) =>
      prev.tabAdoc === next ? prev : { idSin: prev.idSin, tabAdoc: next, category: null },
    );
  }, []);

  const handleCategoryChange = useCallback(
    (value: string | string[]) => {
      const code = Array.isArray(value) ? (value[0] ?? '') : value;
      setFilters((prev) => {
        if (code === '' || code === ALL_CATEGORIES_VALUE) {
          return prev.category === null ? prev : { ...prev, category: null };
        }
        if (prev.category?.code === code) return prev;
        const option = categories.find((c) => c.code === code) ?? { code, label: code };
        return { ...prev, category: option };
      });
    },
    [categories],
  );

  // Header controls resolved against the tenant overrides (identity at baseline).
  const headerFilters = useMemo(
    () => applyFilterOverrides(DOCUMENTS_HEADER_CONFIG.filters, _layoutOverrides?.filters),
    [_layoutOverrides?.filters],
  );
  const popOutOverride = _layoutOverrides?.actions?.[POP_OUT_ACTION_KEY];
  const popOutLabel = popOutOverride?.label ?? POP_OUT_ACTION.label;
  const showPopOut =
    !suppressPopOut && (popOutOverride?.visible ?? POP_OUT_ACTION.visible ?? true);

  const categorySelectId = useId();
  const categoryFilter = headerFilters.find((f) => f.id === CATEGORY_FILTER_KEY);
  const allCategoriesLabel = categoryFilter?.placeholder ?? '';

  // "Toutes les catégories" first, then the GED's categories. A selected
  // category missing from the latest list (e.g. its last document was
  // re-classified) stays listed so the select keeps showing the active filter.
  const categorySelectOptions = useMemo<SelectOption[]>(() => {
    const options: SelectOption[] = [{ value: ALL_CATEGORIES_VALUE, label: allCategoriesLabel }];
    for (const c of categories) options.push({ value: c.code, label: c.label });
    if (category && !categories.some((c) => c.code === category.code)) {
      options.push({ value: category.code, label: category.label });
    }
    return options;
  }, [allCategoriesLabel, categories, category]);

  const renderHeaderFilter = (filter: FilterConfig) => {
    if (filter.id === SCOPE_FILTER_KEY) {
      return (
        <div key={filter.id} role="group" aria-label={filter.label}>
          <ButtonGroup attached size="sm">
            {(filter.options ?? []).map((option) => {
              const value = option.value;
              if (!isDocumentsTabAdoc(value)) return null;
              const active = value === tabAdoc;
              return (
                <Button
                  key={value}
                  variant={active ? 'primary' : 'secondary'}
                  aria-pressed={active}
                  title={DOCUMENTS_HEADER_CONFIG.tooltips.scope[value]}
                  onClick={() => {
                    handleTabAdocChange(value);
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </ButtonGroup>
        </div>
      );
    }
    if (filter.id === CATEGORY_FILTER_KEY) {
      return (
        <div key={filter.id} className="w-56">
          {/* Accessible name from config — the mockup shows no visible label. */}
          <Text as="label" htmlFor={categorySelectId} className="sr-only">
            {filter.label}
          </Text>
          <Select
            id={categorySelectId}
            options={categorySelectOptions}
            value={category?.code ?? ALL_CATEGORIES_VALUE}
            onChange={handleCategoryChange}
            placeholder={filter.placeholder}
            searchable={categories.length > DOCUMENTS_HEADER_CONFIG.categorySearchThreshold}
            size="sm"
          />
        </div>
      );
    }
    return null;
  };

  // Build the per-render rows (data + live selection context) and the merged
  // config. Sort keys are derived once per fetched list; rows are rebuilt
  // whenever the data or selection changes so the renderers' / row-click
  // closures stay fresh. `applyLayoutOverrides` returns the config unchanged at
  // baseline (no rules) so the render is byte-identical.
  const sortableDocuments = useMemo(
    () => documents.map((doc) => ({ doc, sort: buildSortKeys(doc) })),
    [documents],
  );

  const rows = useMemo<DocumentRow[]>(
    () =>
      sortableDocuments.map(({ doc, sort }) => ({
        ...doc,
        id: doc.ID_ADOC,
        __sort: sort,
        __active: doc.ID_ADOC === selectedId,
        __ctx: {
          active: doc.ID_ADOC === selectedId,
          onSelect: () => {
            setSelectedId(doc.ID_ADOC);
          },
        },
      })),
    [sortableDocuments, selectedId],
  );

  const baseConfig = useMemo(() => createDocumentsListConfig(rows), [rows]);

  // Publish the list columns AND the header controls (filters + pop-out
  // action) so the layout editor lists every rule target of this card.
  usePublishLayoutConfig('claimflow.documentsCard', {
    ...buildPublishedConfigFromDataList(baseConfig, DOCUMENTS_CARD_TEXT.title),
    filters: [...DOCUMENTS_HEADER_CONFIG.filters],
    actions: DOCUMENTS_HEADER_CONFIG.actions,
  });

  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const list = useDataList(config);

  const emptyText = category
    ? DOCUMENTS_CARD_TEXT.emptyForCategory
    : DOCUMENTS_CARD_TEXT.empty;

  return (
    <Card>
      <Card.Header>
        <div className="flex flex-wrap items-center justify-between gap-2 w-full">
          <div className="flex items-center gap-2">
            <Icon icon={FileText} size="md" color="primary" />
            <Text as="span" weight="semibold" size="base">
              {resolveCardTitle(DOCUMENTS_CARD_TEXT.title, _layoutOverrides)}
            </Text>
            <Badge variant="default" size="sm" pill>
              {documents.length}
            </Badge>
            {/* A filter change refetches in place: the current rows stay until
                the new ones arrive, with this discreet progress hint. */}
            {isRefetching && <Spinner size="xs" />}
          </div>
          <HStack gap={2} align="center" justify="end" wrap>
            {visibleHeaderFilters(headerFilters).map(renderHeaderFilter)}
            {showPopOut && (
              <Button
                variant={POP_OUT_ACTION.variant}
                size={POP_OUT_ACTION.size}
                leftIcon={<SquareArrowOutUpRight className="w-4 h-4" />}
                onClick={handlePopOut}
                disabled={!tenantSlug}
                title={DOCUMENTS_HEADER_CONFIG.tooltips.popOut}
              >
                {popOutLabel}
              </Button>
            )}
          </HStack>
        </div>
      </Card.Header>
      <Card.Content>
        {isInitialLoading && (
          <div className="flex items-center justify-center min-h-32">
            <Spinner size="md" />
          </div>
        )}

        {!isInitialLoading && loadError && <Alert variant="error">{loadError}</Alert>}

        {!isInitialLoading && !loadError && documents.length === 0 && (
          <Text size="sm" color="muted">
            {emptyText}
          </Text>
        )}

        {!isInitialLoading && !loadError && documents.length > 0 && (
          <VStack gap={4}>
            {/* List caps at ~4 rows; scrolls when there are more. */}
            <div className="border border-secondary-200 rounded-lg overflow-hidden">
              <div
                className="max-h-[228px] overflow-y-auto"
                data-testid="documents-list"
                aria-busy={isRefetching}
              >
                <DataList config={config} list={list}>
                  <DataList.Table />
                </DataList>
              </div>
            </div>

            {!selectedRow && (
              <div className="bg-secondary-50 rounded-lg flex items-center justify-center h-48">
                <VStack gap={2} align="center">
                  <FileText className="w-8 h-8 text-secondary-400" />
                  <Text size="sm" color="muted">
                    {DOCUMENTS_CARD_TEXT.noSelection}
                  </Text>
                </VStack>
              </div>
            )}

            {selectedRow && selectedKind === 'email' && (
              <EmailDetail
                key={selectedRow.ID_ADOC}
                row={selectedRow}
                classementOptions={classementOptions}
                documents={documents}
                idSin={idSin}
                onGedded={() => {
                  void refresh();
                }}
                onReply={handleEmailReply}
              />
            )}

            {selectedRow && selectedKind !== 'email' && (
              <DocumentPreviewConfigurable
                key={selectedRow.ID_ADOC}
                row={selectedRow}
                classementOptions={classementOptions}
                onSaved={() => {
                  void refresh();
                }}
              />
            )}
          </VStack>
        )}
      </Card.Content>
    </Card>
  );
}
